import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle } from 'lucide-react';
import { AppStoreBadges } from '../components/landing/AppStoreBadges';

const plans = [
  {
    key: 'monthly',
    name: 'Monthly',
    price: '9.99',
    period: '/month',
    total: '9.99',
    popular: false,
    features: ['Unlimited disputes', 'AI-powered verdicts', 'Email invitations', 'Full verdict reports'],
  },
  {
    key: 'quarterly',
    name: '3 Months',
    price: '8.99',
    period: '/month',
    total: '26.97',
    discount: '10% off',
    popular: true,
    features: ['Everything in Monthly', 'Save 10% per month', 'Priority verdict generation', 'Dispute history archive'],
  },
  {
    key: 'biannual',
    name: '6 Months',
    price: '7.99',
    period: '/month',
    total: '47.94',
    discount: '20% off',
    popular: false,
    features: ['Everything in 3 Months', 'Save 20% per month', 'Extended dispute archive', 'Advanced analytics'],
  },
  {
    key: 'yearly',
    name: 'Yearly',
    price: '6.99',
    period: '/month',
    total: '83.88',
    discount: '30% off',
    popular: false,
    hero: true,
    features: ['Everything in 6 Months', 'Save 30% per month', 'Unlimited dispute archive', 'Priority support', 'Early access to new features'],
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="pricing-page">
      <div className="text-center mb-12 sm:mb-16">
        <span className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-primary">Pricing</span>
        <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-serif font-bold" data-testid="pricing-title">
          Choose Your Plan
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
          Start resolving disputes today. All plans include AI-powered verdicts and full reports.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
        {plans.map((plan) => (
          <Card
            key={plan.key}
            className={`rounded-2xl border shadow-sm card-hover relative ${plan.popular ? 'border-primary shadow-md ring-1 ring-primary/20' : ''}`}
            data-testid={`pricing-card-${plan.key}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground rounded-full px-3 py-0.5 text-xs">
                  Most Popular
                </Badge>
              </div>
            )}
            {plan.discount && (
              <div className="absolute top-3 right-3">
                <Badge variant="outline" className="text-xs text-secondary border-secondary/30">
                  {plan.discount}
                </Badge>
              </div>
            )}
            <CardHeader className="pt-6 pb-2">
              <CardTitle className="font-serif text-lg">{plan.name}</CardTitle>
              <CardDescription className="text-xs">{plan.hero ? 'Best value' : 'Billed as one payment'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-serif font-bold">£{plan.price}</span>
                  <span className="text-sm text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">£{plan.total} total</p>
              </div>
              <ul className="space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                className="w-full rounded-full opacity-70 cursor-not-allowed"
                variant={plan.popular ? 'default' : 'outline'}
                disabled
                data-testid={`plan-cta-${plan.key}`}
              >
                Available in the app
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-16 text-center space-y-6" data-testid="pricing-download">
        <div>
          <h2 className="text-xl sm:text-2xl font-serif font-bold tracking-tight">Ready to get started?</h2>
          <p className="text-sm text-muted-foreground mt-2">Download the VerdictIQ mobile app to subscribe and start resolving disputes.</p>
        </div>
        <div className="flex justify-center">
          <AppStoreBadges idSuffix="pricing-page" />
        </div>
        <p className="text-xs text-muted-foreground pt-4">Full money-back guarantee. Cancel anytime.</p>
      </div>
    </div>
  );
}
